/**
 * IntakeSourceFormDialog — J7-W7 / Sheet Migration Wave 5
 *
 * Create + Edit dual-mode dialog for Intake Sources.
 * Fields: title, description, connector_type (Select), connector_config (JSON textarea).
 *
 * Sheet Migration W5: converted from Modal to FormSheet with useFormSnapshot.
 * URL routing supported via optional urlKey prop (urlId derived from editSource.id).
 *
 * Create mode: POST /api/v1/intake/sources
 * Edit mode:   PUT  /api/v1/intake/sources/:id
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { t } from '@/lib/i18n';
import { FormSheet } from '@/components/ui/FormSheet';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { Alert } from '@/components/ui/Alert';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { showToast } from '@/components/ui/Toast';
import {
  useRorosIntakeSourceCreate,
  useRorosIntakeSourceUpdate,
} from '@/hooks/roros/intake';
import type { RorosIntakeSourceDTO } from '@/services/roros/intake.service';

type ConnectorType = 'manual' | 'webhook' | 'email' | 'api';

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

type IntakeSourceSnapshot = {
  title: string;
  description: string;
  connectorType: ConnectorType;
  connectorConfigText: string;
};

function diffIntakeSourceSnapshots(
  base: IntakeSourceSnapshot,
  cur: IntakeSourceSnapshot,
): Partial<Record<keyof IntakeSourceSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    connectorType: base.connectorType !== cur.connectorType,
    connectorConfigText: base.connectorConfigText !== cur.connectorConfigText,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseConfig(text: string): { ok: boolean; value: Record<string, unknown> | null; error?: string } {
  const trimmed = text.trim();
  if (!trimmed) return { ok: true, value: null };
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
      return { ok: false, value: null, error: t('Connector config must be a JSON object') };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch {
    return { ok: false, value: null, error: t('Connector config must be valid JSON') };
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface FieldErrors {
  title?: string;
  description?: string;
  connectorConfigText?: string;
  general?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /** When provided the dialog runs in edit mode. */
  editSource?: RorosIntakeSourceDTO | null;
  /**
   * URL routing (opt-in): when set, FormSheet syncs ?urlKey=<editSource.id>
   * in the URL. Omit to opt out — existing callers need no changes.
   */
  urlKey?: string;
}

export function IntakeSourceFormDialog({ isOpen, onClose, onSaved, editSource, urlKey }: Props) {
  const isEdit = !!editSource;
  const [errors, setErrors] = useState<FieldErrors>({});

  const createMutation = useRorosIntakeSourceCreate();
  const updateMutation = useRorosIntakeSourceUpdate();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // ─── Form state via useFormSnapshot ──────────────────────────────────────
  // depKey = editSource?.id so the snapshot re-seeds when the edited source changes.
  const { state, setField, baseline, current } = useFormSnapshot<IntakeSourceSnapshot>({
    initialFactory: () =>
      editSource
        ? {
            title: editSource.title,
            description: editSource.description ?? '',
            connectorType: (editSource.connectorType as ConnectorType) || 'manual',
            connectorConfigText: editSource.connectorConfig
              ? JSON.stringify(editSource.connectorConfig, null, 2)
              : '',
          }
        : {
            title: '',
            description: '',
            connectorType: 'manual',
            connectorConfigText: '',
          },
    isOpen,
    depKey: editSource?.id ?? null,
  });

  // Reset validation errors on open.
  useEffect(() => {
    if (isOpen) setErrors({});
  }, [isOpen, editSource?.id]);

  // ─── Validation ───────────────────────────────────────────────────────────

  const validate = (): { ok: boolean; errs: FieldErrors; connectorConfig?: Record<string, unknown> | null } => {
    const e: FieldErrors = {};
    const title = state.title.trim();
    if (title.length < 3) e.title = t('Title must be at least 3 characters');
    else if (title.length > 255) e.title = t('Title must be 255 characters or fewer');

    if (!state.description.trim()) e.description = t('Description is required');

    let connectorConfig: Record<string, unknown> | null = null;
    if (state.connectorType !== 'manual') {
      const { ok, value, error } = parseConfig(state.connectorConfigText);
      if (!ok) e.connectorConfigText = error;
      else connectorConfig = value;
    }

    return { ok: Object.keys(e).length === 0, errs: e, connectorConfig };
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const { ok, errs, connectorConfig } = validate();
    if (!ok) {
      setErrors(errs);
      return;
    }

    const payload = {
      title: state.title.trim(),
      description: state.description.trim(),
      connector_type: state.connectorType,
      connector_config: connectorConfig,
    };

    if (isEdit && editSource) {
      updateMutation.mutate(
        { id: editSource.id, payload },
        {
          onSuccess: () => {
            showToast.success(t('Intake source updated'));
            onClose();
            onSaved?.();
          },
          onError: (err: Error) => {
            setErrors({ general: err.message || t('Failed to update intake source') });
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => {
          showToast.success(t('Intake source created'));
          onClose();
          onSaved?.();
        },
        onError: (err: Error) => {
          setErrors({ general: err.message || t('Failed to create intake source') });
        },
      });
    }
  };

  // ─── Options ──────────────────────────────────────────────────────────────

  const connectorTypeOptions = [
    { value: 'manual', label: t('Manual') },
    { value: 'webhook', label: t('Webhook') },
    { value: 'email', label: t('Email') },
    { value: 'api', label: t('API') },
  ];

  return (
    <FormSheet<IntakeSourceSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('Edit Intake Source') : t('New Intake Source')}
      subtitle={t('Intake sources are knowledge items scanned daily by the Intake Scan routine.')}
      mode={isEdit ? 'edit' : 'create'}
      baseline={baseline}
      current={current}
      diff={diffIntakeSourceSnapshots}
      onSubmit={handleSubmit}
      isSubmitting={isPending}
      submitLabel={isEdit ? t('Save changes') : t('Create')}
      submittingLabel={t('Saving…')}
      submitTestId="intake-source-submit-button"
      width="md"
      urlKey={urlKey}
      urlId={editSource?.id}
    >
      <div className="space-y-4">
        {errors.general && (
          <Alert variant="error" className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <span>{errors.general}</span>
          </Alert>
        )}

        <Input
          label={t('Title')}
          value={state.title}
          onChange={(e) => setField('title', e.target.value)}
          error={errors.title}
          required
          data-testid="intake-source-title-input"
        />

        <Textarea
          label={t('Description')}
          value={state.description}
          onChange={(e) => setField('description', e.target.value)}
          error={errors.description}
          required
          rows={3}
          data-testid="intake-source-description-input"
        />

        <div data-testid="intake-source-connector-type-select">
          <Select
            label={t('Connector Type')}
            value={state.connectorType}
            onValueChange={(v) => setField('connectorType', v as ConnectorType)}
            options={connectorTypeOptions}
          />
        </div>

        {state.connectorType !== 'manual' && (
          <Textarea
            label={t('Connector Config (JSON)')}
            value={state.connectorConfigText}
            onChange={(e) => setField('connectorConfigText', e.target.value)}
            error={errors.connectorConfigText}
            placeholder={'{\n  "url": "https://...",\n  "secret": "...\n}'}
            rows={4}
            data-testid="intake-source-connector-config-input"
          />
        )}
      </div>
    </FormSheet>
  );
}
